// src/components/Explore.tsx

import React, { useState, useCallback } from 'react'
import {
  collection,
  query,
  QuerySnapshot,
  where,
  QueryConstraint,
} from 'firebase/firestore'
import { db } from '@/helpers/firebase'
import { FaArrowLeft, FaArrowRight, FaTimes } from 'react-icons/fa'
import { useCachedPublicImage } from '@/helpers/urls'
import '@/screens/Explore.css'
import { useFirestoreCollectionCache } from '@/hooks/useFirestoreCache'

type CardDefinition = {
  cardId: string
  name: string
  category: string
  collection: string
  subcollection: string
  rarity: 'Common' | 'Uncommon' | 'Rare' | 'Epic' | 'Legendary'
  revealRate: number
  description: string
  url: string
}

type ViewState =
  | 'categories'
  | 'collections'
  | 'subcollections'
  | 'cards'
  | 'description'

// Helper component for displaying cards with cached images
const CardItem: React.FC<{
  card: CardDefinition
  onClick: () => void
}> = ({ card, onClick }) => {
  const cachedImageUrl = useCachedPublicImage(card.url)

  return (
    <div
      className="explore__item explore__card-item"
      style={{
        backgroundImage: cachedImageUrl ? `url(${cachedImageUrl})` : 'none',
      }}
      onClick={onClick}
    >
      <div className="explore__item-content">
        <span
          className={`explore__rarity-badge explore__rarity-badge--${card.rarity.toLowerCase()}`}
        >
          {card.rarity}
        </span>
        <h3 className="explore__item-title">{card.name}</h3>
      </div>
    </div>
  )
}

export const Explore: React.FC = () => {
  const [view, setView] = useState<ViewState>('categories')
  const [selectedCategory, setSelectedCategory] = useState<string>('')
  const [selectedCollection, setSelectedCollection] = useState<string>('')
  const [selectedSubcollection, setSelectedSubcollection] = useState<string>('')
  const [selectedCard, setSelectedCard] = useState<CardDefinition | null>(null)
  const [selectedRarity, setSelectedRarity] = useState<string | null>(null)

  // Transform functions for cache hooks
  const transformCards = useCallback((snapshot: QuerySnapshot) => {
    const cards: CardDefinition[] = []
    snapshot.forEach((doc) => {
      cards.push({ ...doc.data(), cardId: doc.id } as CardDefinition)
    })
    return cards
  }, [])

  // Build query constraints based on current view and selections
  const getQueryConstraints = useCallback((): QueryConstraint[] => {
    const constraints: QueryConstraint[] = []

    if (selectedRarity) {
      constraints.push(where('rarity', '==', selectedRarity))
    }

    if (selectedCategory) {
      constraints.push(where('category', '==', selectedCategory))
    }

    if (selectedCollection) {
      constraints.push(where('collection', '==', selectedCollection))
    }

    if (selectedSubcollection) {
      constraints.push(where('subcollection', '==', selectedSubcollection))
    }

    return constraints
  }, [
    selectedCategory,
    selectedCollection,
    selectedSubcollection,
    selectedRarity,
  ])

  // Use cached Firestore collection - now without wallet dependency
  const {
    data: cards = [],
    loading,
    error,
  } = useFirestoreCollectionCache<CardDefinition>(
    query(collection(db, 'cards'), ...getQueryConstraints()),
    `explore-cards-${selectedRarity || 'all'}-${selectedCategory}-${selectedCollection}-${selectedSubcollection}`,
    transformCards,
    true,
    false // requireAuth: false
  )

  // Derived data based on current cards
  const categories = Array.from(new Set(cards.map((card) => card.category)))
  const collections = Array.from(
    new Set(
      cards
        .filter((card) => card.category === selectedCategory)
        .map((card) => card.collection)
    )
  )
  const subcollections = Array.from(
    new Set(
      cards
        .filter(
          (card) =>
            card.category === selectedCategory &&
            card.collection === selectedCollection
        )
        .map((card) => card.subcollection)
    )
  )

  const filteredCards = cards.filter(
    (card) =>
      card.category === selectedCategory &&
      card.collection === selectedCollection &&
      card.subcollection === selectedSubcollection
  )

  // Navigation handlers
  const handleCategoryClick = (category: string) => {
    if (!['Landscape'].includes(category)) {
      alert(`${category} is coming soon!`)
      return
    }
    setSelectedCategory(category)
    setView('collections')
  }

  const handleCollectionClick = (collectionName: string) => {
    setSelectedCollection(collectionName)
    setView('subcollections')
  }

  const handleSubcollectionClick = (subcollectionName: string) => {
    setSelectedSubcollection(subcollectionName)
    setView('cards')
  }

  const handleCardClick = (card: CardDefinition) => {
    setSelectedCard(card)
    setView('description')
  }

  const handleBack = () => {
    switch (view) {
      case 'collections':
        setView('categories')
        setSelectedCategory('')
        break
      case 'subcollections':
        setView('collections')
        setSelectedCollection('')
        break
      case 'cards':
        setView('subcollections')
        setSelectedSubcollection('')
        break
      case 'description':
        setView('cards')
        setSelectedCard(null)
        break
    }
  }

  const handleRarityClick = (rarity: string) => {
    setSelectedRarity(rarity === selectedRarity ? null : rarity)
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner"></div>
        <p>Loading cards...</p>
      </div>
    )
  }

  if (error) {
    return <div className="explore__error">{error.message}</div>
  }

  const renderRarityFilters = () => (
    <div className="explore__filters">
      <h3 className="explore__filter-title">Filter by Rarity</h3>
      <div className="explore__chip-scroll-container">
        <div className="explore__chip-container">
          {['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'].map((rarity) => (
            <button
              key={rarity}
              className={`explore__chip ${selectedRarity === rarity ? 'explore__chip--selected' : ''}`}
              onClick={() => handleRarityClick(rarity)}
            >
              {rarity}
            </button>
          ))}
          {selectedRarity && (
            <button
              className="explore__chip"
              onClick={() => setSelectedRarity(null)}
            >
              <FaTimes /> Clear
            </button>
          )}
        </div>
        <div className="explore__scroll-hint">
          More rarity <FaArrowRight />
        </div>
      </div>
    </div>
  )

  const renderCategories = () => (
    <div className="explore__grid">
      {categories.map((category) => (
        <div
          key={category}
          className={`explore__item explore__menu-item ${
            !['Landscape'].includes(category)
              ? 'explore__item--coming-soon'
              : ''
          }`}
          onClick={() => handleCategoryClick(category)}
        >
          <div className="explore__item-content">
            <h3 className="explore__item-title">{category}</h3>
            {!['Landscape'].includes(category) && (
              <span className="explore__coming-soon-text">Coming Soon</span>
            )}
          </div>
        </div>
      ))}
    </div>
  )

  const renderCollections = () => (
    <div className="explore__grid">
      {collections.map((collectionName) => (
        <div
          key={collectionName}
          className="explore__item explore__menu-item"
          onClick={() => handleCollectionClick(collectionName)}
        >
          <div className="explore__item-content">
            <h3 className="explore__item-title">{collectionName}</h3>
          </div>
        </div>
      ))}
    </div>
  )

  const renderSubcollections = () => (
    <div className="explore__grid">
      {subcollections.map((subcollectionName) => (
        <div
          key={subcollectionName}
          className="explore__item explore__menu-item"
          onClick={() => handleSubcollectionClick(subcollectionName)}
        >
          <div className="explore__item-content">
            <h3 className="explore__item-title">{subcollectionName}</h3>
          </div>
        </div>
      ))}
    </div>
  )

  const renderCards = () => (
    <div className="explore__grid">
      {filteredCards.map((card) => (
        <CardItem
          key={card.cardId}
          card={card}
          onClick={() => handleCardClick(card)}
        />
      ))}
    </div>
  )

  const renderCardDetails = () =>
    selectedCard && (
      <div className="explore__modal" onClick={() => setView('cards')}>
        <div
          className="explore__modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="explore__modal-title">{selectedCard.name}</h2>

          <div className="explore__detail-row">
            <span className="explore__detail-label">Category</span>
            <span className="explore__detail-value">
              {selectedCard.category}
            </span>
          </div>

          <div className="explore__detail-row">
            <span className="explore__detail-label">Collection</span>
            <span className="explore__detail-value">
              {selectedCard.collection}
            </span>
          </div>

          <div className="explore__detail-row">
            <span className="explore__detail-label">Subcollection</span>
            <span className="explore__detail-value">
              {selectedCard.subcollection}
            </span>
          </div>

          <div className="explore__detail-row">
            <span className="explore__detail-label">Rarity</span>
            <span
              className={`explore__detail-value explore__rarity-badge explore__rarity-badge--${selectedCard.rarity.toLowerCase()}`}
            >
              {selectedCard.rarity}
            </span>
          </div>

          <div className="explore__detail-row">
            <span className="explore__detail-label">Description</span>
            <span className="explore__detail-value">
              {selectedCard.description}
            </span>
          </div>
        </div>
      </div>
    )

  return (
    <div className="explore">
      {view !== 'categories' && (
        <button className="explore__back-button" onClick={handleBack}>
          <FaArrowLeft /> Back
        </button>
      )}

      {view === 'categories' && renderRarityFilters()}

      {view === 'categories' && renderCategories()}
      {view === 'collections' && renderCollections()}
      {view === 'subcollections' && renderSubcollections()}
      {view === 'cards' && renderCards()}
      {view === 'description' && renderCardDetails()}
    </div>
  )
}
